export type Metric = { value: string; label: string };

export type Detail = {
  /** Short label for the thing this panel is about. */
  title: string;
  /** The thing that was actually hard. */
  problem: string;
  /** What got built. */
  approach: string;
  /** What it bought, measurably. */
  result: string;
};

export type Shot = { src: string; alt: string };

/** A cover still, optionally upgraded to a muted looping clip. */
export type Cover = Shot & {
  /** MP4 played on loop once it scrolls into view. Poster is `src`. */
  video?: string;
  /** What the clip shows, for anyone who cannot watch it. */
  videoAlt?: string;
};

export type CaseStudy = {
  id: string;
  index: string;
  name: string;
  kind: string;
  org: string;
  period: string;
  /** Team shape. The first thing a reviewer looks for and the easiest to bury. */
  scope: string;
  status?: { label: string; tone: "live" | "active" | "past" };
  /** One sentence a non-engineer understands. */
  summary: string;
  /** Two or three sentences an engineer wants. */
  context: string;
  stack: string[];
  details: Detail[];
  metrics: Metric[];
  /** Small caption under the metrics strip, e.g. when they were measured. */
  metricsNote?: string;
  links?: { label: string; href: string }[];
  shots?: Shot[];
  cover?: Cover;
};

export const CASE_STUDIES: CaseStudy[] = [
  {
    id: "swarmhold",
    index: "01",
    name: "Swarmhold",
    kind: "Real-time multiplayer simulation",
    org: "Peng-O-Labs",
    period: "2025 to present",
    scope: "Sole engineer · architecture, netcode, rendering, tooling, release",
    status: { label: "Demo live on Steam", tone: "active" },
    summary:
      "A four-player cooperative base-defense game where thousands of enemies path, fight and die in real time on every player's machine at once, while movement and combat stay instant for everyone in the session.",
    context:
      "The spec is not only enemies. Ten thousand of them, hundreds of towers firing several times a second, thousands of projectiles in flight, four players, and the visual effects layered over all of it, inside a 16.67 ms frame budget, on every machine in the session rather than just the host's. When a system did not fit, it got rebuilt until it did; nothing was dropped to make the number work. Public demo shipped July 2026, with Steam Next Fest planned ahead of full launch.",
    stack: [
      "Unity 6 LTS",
      "C#",
      "Photon Fusion 2",
      "Burst / Jobs",
      "Steamworks",
    ],
    details: [
      {
        title: "Navigation that stops scaling with the crowd",
        problem:
          "Per-entity pathfinding is linear in agents. At ten thousand agents that is ten thousand independent searches every tick, and the engine's built-in navigation could not be made to fit inside the frame.",
        approach:
          "Replaced it with a flow-field navigation system: one shared integration pass over a custom uniform grid, so an agent reads a direction instead of running a search. The shared pass is the easy half; the work was everything around it. Crowd density is injected into the cost so the swarm routes around its own pile-ups instead of funnelling into one line, and the grid is server-side and never transmitted. A uniform spatial hash is rebuilt every tick with a counting sort so neighbour queries stay bounded however dense the crowd gets, agent state lives in structure-of-arrays buffers so the hot loop stays linear in memory, and the whole pipeline is jobified and Burst-compiled onto worker threads, with a regenerated field published by buffer swap rather than per-cell copy. The field is not the answer to every path: its cost is fixed per field rather than per agent, which wins at ten thousand and loses at ten, so low-population types such as players and bosses keep per-entity A*. Which system an entity navigates on falls out of the cost model, not preference.",
        result:
          "Navigation became O(cells) instead of O(entities), so the ten-thousandth enemy costs what the four-hundredth costs and the grid does not care how many more arrive. Separating the navigation band from the visual band then cut the grid from 48,400 cells to 28,900 with no gameplay change at all. The hash that keeps separation cheap is the same structure every turret acquires through and every projectile resolves against. The memory layout is what lets the jobs vectorize. Navigation being independent of population is what later made the replication tiers affordable.",
      },
      {
        title: "A transport that does not scale with the horde",
        problem:
          "One networked object per enemy saturates the transport long before ten thousand. The reliable channel is scarce in messages rather than bytes, and the host's uplink is multiplied by every connected client, so the naive approach spends the entire budget on crowd positions and leaves nothing for the things players actually feel.",
        approach:
          "Built a three-tier replication model: a premium tier on the engine's snapshot channel, an overflow tier driven by the server over a multiplexed custom pipe at 16 Hz, and a bulk tier running a deterministic client-side simulation on every peer, driven by replicated inputs alone. Projectiles, tower fire, damage flashes and audio cues were all migrated to derive locally from state the client already has, rather than sending anything.",
        result:
          "Thousands of enemies now cost zero bandwidth: they are derived, not sent, and the flow field itself never crosses the wire. That turns bandwidth into something I get to spend deliberately, and it goes to player movement and combat feel first, then to the handful of enemies close enough to deserve authoritative positions. The multiplexer structurally cannot saturate the channel either, capping at 16 flushes per second against 21 to 24 delivered.",
      },
      {
        title: "The ceiling was rendering, not thinking",
        problem:
          "Skinned meshes cannot batch, so every visible enemy cost its own draw call and bone-palette upload at roughly 26 µs each, which caps the horde near 400 on screen. Simulation was never the bottleneck: 800 fully simulated enemies run at about 180 fps with visuals off.",
        approach:
          "Baked skeletal animation into vertex-animation textures and moved the entire enemy roster onto GPU instancing. A single Burst job builds the transform buffer with frustum culling, then submits in chunks of 1,023 instances per draw.",
        result:
          "Per-enemy render cost collapsed to a flat 1 to 1.5 ms at 2,000 instances. On identical hardware, 1,000 concurrent enemies went from 52 fps to 84 fps average, and the worst single second of the whole ladder was 76 fps against a baseline minimum of 27. At 4,000 enemies it holds 84 fps, ten times the population the skinned path could put on screen. What remains scales with enemies in frame rather than enemies alive, which is what turns the population cap back into a design decision.",
      },
    ],
    metrics: [
      { value: "10,000", label: "entities inside the frame budget" },
      { value: "84 fps", label: "at 4,000 enemies" },
      { value: "O(cells)", label: "pathfinding, not O(agents)" },
      { value: "0 B", label: "for thousands of enemies" },
    ],
    links: [
      {
        label: "Play the demo on Steam",
        href: "https://store.steampowered.com/app/3965480/Swarmhold/",
      },
    ],
    cover: {
      src: "/media/swarmhold-gameplay-poster.webp",
      alt: "A grid of Swarmhold towers holding a line against an incoming swarm",
      video: "/media/swarmhold-gameplay.mp4",
      videoAlt:
        "Swarmhold gameplay: a dense grid of towers holds a line while a swarm pours in from every side and shells burst along the wall.",
    },
    shots: [
      {
        src: "/media/swarmhold-horde.webp",
        alt: "Hundreds of enemies converging on a walled player base in Swarmhold",
      },
      {
        src: "/media/swarmhold-blueprint.webp",
        alt: "Blueprint draft screen in Swarmhold",
      },
      {
        src: "/media/swarmhold-snowfield.webp",
        alt: "Swarmhold arctic biome under assault",
      },
    ],
  },
  {
    id: "qualtrics",
    index: "02",
    name: "Analytics visualization platform",
    kind: "Enterprise data visualization",
    org: "Qualtrics",
    period: "2022 to 2024",
    scope:
      "Widgets & Data Visualization team · delivered across Product, UX and two partner engineering teams",
    status: { label: "Shipped to production", tone: "past" },
    summary:
      "Client-facing analytics widgets in React, TypeScript and D3 for enterprise dashboards, taken from concept through launch on the Widgets and Data Visualization team.",
    context:
      "Two years on the team that owns how customers actually see their data. The work split three ways: building new visualizations, keeping a large legacy surface alive, and removing the friction that was slowing everyone else down.",
    stack: ["React", "TypeScript", "D3.js", "AngularJS", "Jenkins", "Splunk"],
    details: [
      {
        title: "A flagship visualization, four teams deep",
        problem:
          "A flagship visualization had to go from concept to production across Product, UX and two partner engineering teams, over multiple quarters.",
        approach:
          "Owned the technical design and drove the cross-functional delivery. Rebuilt in React, TypeScript and D3, coordinating scope and sequencing across every team that touched it.",
        result: "Launched on schedule, and early-access adoption grew tenfold.",
      },
      {
        title: "Two kinds of location, and the API we did not pick",
        problem:
          "A map widget had to plot two different kinds of location on the same map: coordinate pairs, which are unambiguous, and free text that a customer had typed into a survey, which is not. Somebody entering their own city does not spell it the way the next person does, and nothing can be plotted until that text resolves to a real place.",
        approach:
          "Google's Places API resolved messy input better and corrected it as it was typed, and it cost significantly more. I argued for it, because the capability gap was real and my team would be the one carrying it. The decision went the other way, and it went the other way for a reason no technical evaluation would have produced: another team already held a Mapbox contract, which made Mapbox materially cheaper for us than its list price. I built the widget on Mapbox with the resolution gap accepted as ours to absorb.",
        result:
          "Both input paths shipped, well under what the option I had argued for would have cost. The deciding input came out of a conversation with the PM about what the rest of the company was already paying for, which is the part I would not have found on my own.",
      },
      {
        title: "Half the code, and somewhere to look when it breaks",
        problem:
          "The core visualization codebase had accumulated years of parallel implementations, and on-call had no visibility into which widgets were actually slow.",
        approach:
          "Refactored the shared rendering layer down to a single path, then led the migration from Sumo Logic to Splunk, building the alerting dashboards and the team-wide widget performance metrics that came with it.",
        result:
          "Roughly half the lines removed with performance and maintainability both improved, and keep-the-lights-on load down about 20%.",
      },
      {
        title: "Federal authorization, and builds that stopped hurting",
        problem:
          "Federal customers required image-handling controls the platform did not have, and build times were a tax on every single change.",
        approach:
          "Implemented the image security controls needed for FedRAMP authorization, then rebuilt the CI/CD pipelines around parallelized stages and dependency caching.",
        result:
          "Federal authorization achieved, build times down 66% and deployment duration down 50%.",
      },
    ],
    metrics: [
      { value: "FedRAMP", label: "authorized, on controls I implemented" },
      { value: "4 teams", label: "Product, UX and two partner eng orgs" },
      { value: "10×", label: "early-access adoption after the rebuild" },
      { value: "−66%", label: "CI build time after the pipeline rework" },
    ],
  },
  {
    id: "pier-pals",
    index: "03",
    name: "Pier Pals",
    kind: "Shipped commercial product",
    org: "Peng-O-Labs",
    period: "2026",
    scope:
      "Two developers, both on everything · concept to store page in four months",
    status: { label: "Live in Early Access", tone: "live" },
    summary:
      "A cozy idle fishing game that lives in the corner of your desktop while you work, released on Steam in Early Access.",
    context:
      "Built and shipped end to end, covering engine work, networking, UI, localization, store page, trailer and launch. The interesting constraint is inverted from most games: it is a 480×240 always-on-top window with a transparent background that has to stay legible and pleasant while the player is doing something else entirely.",
    stack: [
      "Godot 4.6",
      "GDScript",
      "UI / UX",
      "Steam P2P",
      "Steamworks",
      "Localization",
    ],
    details: [
      {
        title: "Co-op with no servers and no accounts",
        problem:
          "Four-player co-op with no dedicated servers, no accounts, and no backend anyone wants to pay for.",
        approach:
          "Steam P2P over the lobby API, with automatic lobby on launch, friend invites and a public browser, and a hard line drawn between synced state (fishing, catches, chat, emotes, cosmetics, presence) and local state (inventory, currency, saves).",
        result:
          "Drop-in co-op for four players at zero server cost, with no account system to build or maintain.",
      },
      {
        title: "How do you make a 480 by 240 window readable?",
        problem:
          "The whole game is a 480×240 window parked in the corner of someone's screen, beside whatever they are actually doing. Every panel, grid and label competes for that space, it has to stay legible at the smallest window size rather than the one I happened to test at, and six locales mean string lengths vary wildly. Looking fine on my own monitor is not a check.",
        approach:
          "This is where the frontend years paid for themselves. Layouts derive from the live visible rect rather than hardcoded sizes. Content is sized from a capped box that the smallest body can hold at the largest variant, so the longest label in the worst locale still fits. Labels clip and ellipsize before controls yield. Nothing runs to a panel edge. A headless checker then drives every panel and asserts each visible control's rectangle against the viewport bounds. Colour conventions came from WCAG contrast guidance rather than from eye.",
        result:
          "Overflow became a caught regression instead of a bug report, which is what made shipping six languages at once tractable, and the UI reads as deliberate at the smallest size rather than merely fitting.",
      },
      {
        title: "Hearing about save trouble before the player writes in",
        problem:
          "A save file that lives only on the player's disk becomes a support burden the moment anything goes wrong.",
        approach:
          "Per-account save directories, Steam Cloud sync, and an automatic trouble reporter with a write-ahead outbox that survives a crash and retries at next launch.",
        result:
          "Save incidents arrive as structured reports before players think to write in, behind a visible off switch, because silent telemetry is not acceptable.",
      },
    ],
    metrics: [
      { value: "Aug 2026", label: "shipped to Steam Early Access" },
      { value: "14 h", label: "median play time" },
      { value: "83%", label: "still playing past the first hour" },
      { value: "100%", label: "positive reviews" },
    ],
    metricsNote: "As of August 2026",
    links: [
      {
        label: "View on Steam",
        href: "https://store.steampowered.com/app/4643120/",
      },
      { label: "Watch the trailer", href: "https://youtu.be/xAYvoEyQt00" },
    ],
    cover: {
      src: "/media/pierpals-gameplay-poster.webp",
      alt: "Four Pier Pals anglers fishing together in a small window parked over a desktop wallpaper",
      video: "/media/pierpals-gameplay.mp4",
      videoAlt:
        "Pier Pals running as a small always-on-top window over a desktop, with four players fishing and catches popping up.",
    },
    shots: [
      {
        src: "/media/pierpals-shot5.webp",
        alt: "A Pier Pals angler catching a Grade S tuna",
      },
      {
        src: "/media/pierpals-cosmetics.webp",
        alt: "Pier Pals cosmetics panel with a paperdoll preview",
      },
      {
        src: "/media/pierpals-shot3.webp",
        alt: "Several players fishing together on a shared Pier Pals pier",
      },
    ],
  },
];

/** Smaller things worth naming, but not worth a case study. */
export const SIDE_WORK = [
  {
    name: "pengolabs.com",
    blurb:
      "Studio site, plus the API every shipped build reports into: report intake with per-product keys and ticket routing, and gameplay telemetry ingested into Postgres.",
    stack: ["Next.js", "TypeScript", "Node.js", "PostgreSQL"],
    href: "https://pengolabs.com",
  },
  {
    name: "This site",
    blurb:
      "React and TypeScript, no framework. The swarm simulation and every chart on this page are hand-written WebGL2 and D3, with no library underneath either.",
    stack: ["React", "TypeScript", "WebGL2", "D3"],
    href: "https://github.com/jaehan98/my-portfolio",
  },
  {
    name: "Pebbl.",
    blurb:
      "Cross-platform photobooth app. Co-developed the MVP: UI/UX, onboarding, analytics. Shipped to the App Store, then handed off.",
    stack: ["React Native", "TypeScript"],
    href: "https://www.pengolabs.com/projects/pebbl",
  },
  {
    name: "Brainy Cards",
    blurb:
      "Flashcard study app. Sole developer of the MVP used to secure funding, with a full engineering handoff at launch.",
    stack: ["Kotlin", "Firestore"],
    href: "https://play.google.com/store/apps/details?id=com.brainycards&hl=en_US",
  },
];
